// Enter in the customer search, looking the customer up first (opt-in; the
// Fashion layout turns it on). Without it, Enter acts on whatever the 350ms
// debounced search last returned, so a number typed and entered quickly opened
// registration for a customer who already exists. With it, Enter searches the
// typed text straight away and waits for the answer:
//   no customer → registration, prefilled with what was typed
//   one customer → that customer is selected
//   several → the list stays open to pick from
// A second Enter while the lookup runs is ignored.
import { useEffect, useRef, useState } from "react";
import { getCustomer } from "../customer";
import { LAYOUT } from "../../../../utils/fashion/layout";

const noop = () => {};

export default function useLookupFirstEnter({
  enabled,
  tillData,
  customerSearchInput,
  customerSearchResults,
  setCustomerSearchResults,
  setLoading,
  selectCustomer,
  showAddNewCustomerFields,
  onLookupFailed = noop,
  search = getCustomer,
}) {
  const inFlightRef = useRef(false);
  const controllerRef = useRef(null);
  // The rows this Enter found, acted on once the parent has re-rendered with
  // them (selectCustomer reads the parent's list by index).
  const [found, setFound] = useState(null);

  useEffect(() => () => controllerRef.current?.abort(), []);

  useEffect(() => {
    if (!found) return;
    if (customerSearchResults !== found.rows) {
      // A slower search overwrote the list; put this lookup's rows back once.
      if (found.tries < 1) {
        setCustomerSearchResults(found.rows);
        setFound({ ...found, tries: found.tries + 1 });
      } else setFound(null);
      return;
    }
    setFound(null);
    if (found.rows.length === 1) selectCustomer(0);
    else if (found.rows.length === 0) showAddNewCustomerFields();
  }, [found, customerSearchResults]); // eslint-disable-line react-hooks/exhaustive-deps

  const onEnter = async () => {
    const term = String(customerSearchInput ?? "");
    if (term.trim() === "" || term.startsWith(" ")) return;
    if (inFlightRef.current || found) return;
    inFlightRef.current = true;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    let rows;
    let failed = false;
    try {
      await search(null, LAYOUT.FASHION, term.trim(), noop, (r) => (rows = r), null, tillData, null, noop, false, noop, { signal: controller.signal });
    } catch {
      failed = true;
    } finally {
      inFlightRef.current = false;
      if (controllerRef.current === controller) setLoading(false);
    }
    if (controller.signal.aborted) return;
    if (failed || !Array.isArray(rows)) {
      onLookupFailed();
      return;
    }
    setCustomerSearchResults(rows);
    setFound({ rows, tries: 0 });
  };

  // The current behaviour, unchanged, when the option is off.
  const onEnterAsBefore = (searching) => {
    if (searching) return;
    if (customerSearchResults?.length === 1) selectCustomer(0);
    else if (customerSearchResults?.length === 0 && customerSearchInput.trim() !== "" && !customerSearchInput.startsWith(" ")) showAddNewCustomerFields();
  };

  return { onPressEnter: (loading) => (enabled ? onEnter() : onEnterAsBefore(loading)) };
}
